/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import axios from "axios";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import { useTheme } from "@material-ui/core/styles";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import ChatIcon from "@material-ui/icons/Chat";
import PersonIcon from "@material-ui/icons/Person";
import DescriptionIcon from "@material-ui/icons/Description";
import SuperscriptText from "../components/common/SuperscriptText";
import SpotNotesTooltipIcon from "./common/SpotNotesTooltipIcon";

import types from "../helpers/types";
import {
  getTimeSpanRemainingStringFromUTC,
  formatTimeWithTimeZone,
} from "../helpers/dateHelpers";
import { fetchSpotLegById } from "../slices/spotLegSlice";

const StyledTableRow = styled(
  ({ selected, openedRoute, row, theme, ...props }) => <TableRow {...props} />
)`
  ${(props) => {
    if (props.selected) {
      return `background-color: ${props.theme.palette.primary.hover} !important;`;
    } else {
      return `
        &:nth-of-type(odd) {
          background-color: ${props.theme.palette.primary.lighter} !important;
        }
      
        &:nth-of-type(even) {
          background-color: white !important;
        }
      
        &:hover {
          cursor: pointer;
          background-color: ${props.theme.palette.primary.hover} !important;
        }
      `;
    }
  }}
`;

const StyledTableCell = styled(TableCell)`
  text-align: left;
  height: 40px;
  background-color: inherit;
  border-bottom: none !important;
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledPersonIcon = styled(({ theme, ...otherProps }) => (
  <PersonIcon {...otherProps} />
))`
  color: ${(props) => props.theme.palette.primary.yellow};
`;

const StyledDescriptionIcon = styled(({ theme, ...otherProps }) => (
  <DescriptionIcon {...otherProps} />
))`
  color: ${(props) => props.theme.palette.primary.green};
`;

const StyledChatIcon = styled(({ theme, ...otherProps }) => (
  <ChatIcon {...otherProps} />
))`
  color: ${(props) => props.theme.palette.primary.blue};
`;

const StyledChatStatusIcon = styled(({ isRead = false, theme, ...otherProps }) => (
  <ChatIcon {...otherProps} />
))`
  color: ${(props) => props.isRead ?
    props.theme.palette.grey[500] :
    props.theme.palette.primary.blue};
`;

const getWindow = (time) => {
  const timeSpan = getTimeSpanRemainingStringFromUTC(time);
  if (timeSpan.startsWith("-")) {
    return (
      <Typography color="error" component="span">
        {timeSpan}
      </Typography>
    );
  }
  return timeSpan;
};

const SpotLegsRow = ({
  row,
  selected,
  isUserCarrier,
  handleNotification,
  unsavedChanges,
  setUnsavedModalOpen
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const serviceLevel = types.serviceLevels.find(
    (x) => x.id === row.serviceLevel
  );
  const driverType = types.spotDriverTypes.find((x) => x.id === row.driverType);
  const trailerType = types.spotTrailerTypes.find(
    (x) => x.id === row.spotTrailers[0].trailerType
  );
  const requirements = [];
  if (row.uiia) requirements.push("UIIA");
  if (row.doubles) requirements.push("Doubles");

  const getTrailerNumbers = () => {
    const trailerNumbers = [];
    for (let i = 0; i < row.spotTrailers.length; i++) {
      if (row.spotTrailers[i].trailerNumber) {
        trailerNumbers.push(row.spotTrailers[i].trailerNumber);
      }
    }
    return trailerNumbers.join("; ");
  };

  const handleRowClick = () => {
    if (unsavedChanges) {
      setUnsavedModalOpen(true);
    }
    dispatch(fetchSpotLegById(row.spotLegsId, handleNotification));
  };

  const getSortTypeCode = (sortTypeValue) => {
    const matchingSortType = Object.values(types.sortTypes).find(
      (type) => type.value === sortTypeValue
    );
    return matchingSortType?.code ?? "";
  };

  return (
    <StyledTableRow
      hover={true}
      theme={theme}
      tabIndex={-1}
      key={row.code}
      onClick={handleRowClick}
      row={row}
      selected={selected}
      data-testid={`spot-leg-row-${row.spotLegsId}`}
    >
      <StyledTableCell align="left" size="small">
        {
          (row.noteReadStatus === types.noteReadStatus.read ||
            row.noteReadStatus === types.noteReadStatus.unread) &&
          <SpotNotesTooltipIcon noteReadStatus={row.noteReadStatus} spotLegId={row.spotLegsId} />
        }
      </StyledTableCell>
      <StyledTableCell
        align="left"
        size="small"
        onClick={(e) => { e.stopPropagation(); }}
      >
        {row.spotLegsId}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.creationType === 1 ? (
          <StyledPersonIcon theme={theme} />
        ) : (
          <StyledDescriptionIcon theme={theme} />
        )}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.scheduledDepartureDateTimeUTC &&
          getWindow(row.scheduledDepartureDateTimeUTC)}
      </StyledTableCell>
      {!isUserCarrier && (
        <StyledTableCell align="left" size="small">
          {row.status && types.spotLegStatusDescriptions[row.status]}
        </StyledTableCell>
      )}
        <StyledTableCell
          align="left"
          size="small"
          onClick={(e) => { e.stopPropagation(); }}
        >
          {row.upsRouteId ?? "--"}
        </StyledTableCell>
      <StyledTableCell
        align="left"
        size="small"
        onClick={(e) => { e.stopPropagation(); }}
      >
        {row.routeCode ?? "--"}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.movementOriginFacility && (
          <>
            {row.movementOriginFacility.slicCode}{" "}
            {row.movementOriginFacility.alphaCode}
          </>
        )}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.scheduledDepartureDateTimeUTC &&
          formatTimeWithTimeZone(
            row.scheduledDepartureDateTimeUTC,
            row.movementOriginFacility.timeZoneName
          )}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.movementDestinationFacility && (
          <>
            {row.movementDestinationFacility.slicCode}{" "}
            {row.movementDestinationFacility.alphaCode}
          </>
        )}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.scheduledArrivalDateTimeUTC &&
          formatTimeWithTimeZone(
            row.scheduledArrivalDateTimeUTC,
            row.movementDestinationFacility.timeZoneName
          )}
      </StyledTableCell>
      <StyledTableCell
        align="left"
        style={{ textTransform: "capitalize" }}
        size="small"
      >
        {row.movementOriginFacility?.city?.toLowerCase()}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.movementOriginFacility?.stateCode}
      </StyledTableCell>
      <StyledTableCell
        align="left"
        style={{ textTransform: "capitalize" }}
        size="small"
      >
        {row.movementDestinationFacility?.city?.toLowerCase()}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.movementDestinationFacility?.stateCode}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.distance}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        <Tooltip title={serviceLevel?.description ?? ""}>
          <span>{serviceLevel?.code}</span>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell
        align="left"
        size="small"
        onClick={(e) => { e.stopPropagation(); }}
      >
        {row.spotTrailers?.length ? getTrailerNumbers() : ""}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {trailerType?.title}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {driverType?.title}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {requirements.join(", ")}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        <Tooltip
          title={
            row.assignedUser?.id
              ? row.assignedUser.firstName + " " + row.assignedUser.lastName
              : ""
          }
        >
          <span>
            {row.assignedUser?.id
              ? row.assignedUser.firstName + " " + row.assignedUser.lastName
              : "N/A"}
          </span>
        </Tooltip>
        {/*TODO: put in assigned username */}
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.loadOriginFacility?.slicCode
          ? row.loadOriginFacility.slicCode +
          " " +
          row.loadOriginFacility?.alphaCode
          : "--"}
        <SuperscriptText>
          {getSortTypeCode(row.loadOriginSortType)}
        </SuperscriptText>
      </StyledTableCell>
      <StyledTableCell align="left" size="small">
        {row.loadDestinationFacility?.slicCode
          ? row.loadDestinationFacility.slicCode +
          " " +
          row.loadDestinationFacility?.alphaCode
          : "--"}
        <SuperscriptText>
          {getSortTypeCode(row.loadDestinationSortType)}
        </SuperscriptText>
      </StyledTableCell>
    </StyledTableRow>
  );
};

SpotLegsRow.propTypes = {
  row: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  isUserCarrier: PropTypes.bool.isRequired,
  setUnsavedModalOpen: PropTypes.func.isRequired,
  unsavedChanges: PropTypes.bool.isRequired,
};

export default SpotLegsRow;
